import React from "react";
import {
	CartesianGrid,
	Legend,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	type TooltipProps,
	XAxis,
	YAxis,
} from "recharts";
import "./TimeSeriesChart.css";

/** Единицы для байт/с: масштаб и подпись */
const BYTES_PER_SEC_UNITS: { scale: number; label: string }[] = [
	{ scale: 1, label: "B/s" },
	{ scale: 1e3, label: "kB/s" },
	{ scale: 1e6, label: "MB/s" },
	{ scale: 1e9, label: "GB/s" },
	{ scale: 1e12, label: "TB/s" },
];

/**
 * Форматирует значение в байтах/с так, чтобы число было не больше двухзначного:
 * B/s → kB/s → MB/s → GB/s → TB/s.
 */
export function formatBytesPerSec(value: number): string {
	if (!Number.isFinite(value) || value < 0) return "0 B/s";
	if (value === 0) return "0 B/s";
	let i = 0;
	const units = BYTES_PER_SEC_UNITS;
	while (i < units.length - 1 && value / units[i].scale >= 100) {
		i++;
	}
	const { scale, label } = units[i];
	const num = value / scale;
	const s =
		num >= 99.995 ? num.toFixed(0) : num >= 9.995 ? num.toFixed(1) : num.toFixed(2);
	return `${s} ${label}`;
}

export interface SeriesConfig {
	key: string;
	name: string;
	color?: string;
}

export interface TimeSeriesPoint {
	timestamp: string;
	[key: string]: string | number;
}

interface TimeSeriesChartProps {
	data: TimeSeriesPoint[];
	series: SeriesConfig[];
	height?: number;
	yAxisLabel?: string;
	/** Форматировать значения оси Y и тултипа как байт/с (B/s → kB/s → MB/s …) */
	formatAsBytesPerSec?: boolean;
}

export const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({
	data,
	series,
	height = 260,
	yAxisLabel,
	formatAsBytesPerSec = false,
}) => {
	const yAxisTickFormatter = formatAsBytesPerSec
		? (value: number) => formatBytesPerSec(value)
		: undefined;

	const tooltipFormatter: NonNullable<TooltipProps["formatter"]> = formatAsBytesPerSec
		? (value, name) => [formatBytesPerSec(Number(value)), name ?? ""]
		: (value, name) => [String(Number(value)), name ?? ""];

	return (
		<div className="time-series-chart">
			<ResponsiveContainer width="100%" height={height}>
				<LineChart
					data={data}
					margin={{ top: 10, right: 20, bottom: 5, left: 0 }}
				>
					<CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
					<XAxis dataKey="timestamp" stroke="var(--color-text-muted)" />
					<YAxis
						stroke="var(--color-text-muted)"
						tickFormatter={yAxisTickFormatter}
						label={
							yAxisLabel
								? {
										value: yAxisLabel,
										angle: -90,
										position: "insideLeft",
										fill: "var(--color-text-muted)",
									}
								: undefined
						}
					/>
					<Tooltip
						contentStyle={{
							backgroundColor: "var(--color-surface-overlay)",
							border: "1px solid var(--color-border-subtle)",
							color: "var(--color-text)",
						}}
						formatter={tooltipFormatter}
					/>
					<Legend />
					{series.map((s) => (
						<Line
							key={s.key}
							type="monotone"
							dataKey={s.key}
							name={s.name}
							stroke={s.color ?? "#3b82f6"}
							dot={false}
							strokeWidth={2}
							activeDot={{ r: 4 }}
						/>
					))}
				</LineChart>
			</ResponsiveContainer>
		</div>
	);
};
